import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Course } from '../../../mocks/courses';
import CoursePaymentModal from '../../course/components/CoursePaymentModal';

interface Props {
  course: Course;
  highlight?: string;
}

const highlightText = (text: string, keyword: string) => {
  if (!keyword.trim()) return <span>{text}</span>;
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === keyword.toLowerCase() ? (
          <mark key={i} className="bg-white/30 text-white rounded px-0.5 not-italic font-bold">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

const highlightDark = (text: string, keyword: string) => {
  if (!keyword.trim()) return <span>{text}</span>;
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === keyword.toLowerCase() ? (
          <mark key={i} className="bg-violet-100 text-violet-700 rounded px-0.5 not-italic font-semibold">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

const CourseCard = ({ course, highlight = '' }: Props) => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [enrolled, setEnrolled] = useState(false);
  const isPurchased = Boolean(course.isPurchased) || enrolled;
  const isFree = Number(course.price) <= 0;
  const hasDiscount = Number(course.originalPrice) > Number(course.price) && !isFree;
  const discountPct = hasDiscount ? Math.round((1 - course.price / course.originalPrice) * 100) : 0;
  const hasMemberPrice = Number(course.memberPrice ?? 0) > 0 && Number(course.memberPrice) < Number(course.price);

  const handleCardClick = () => navigate(`/course/${course.id}`);

  const handleQuickBuy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPurchased) {
      handleCardClick();
      return;
    }
    setShowModal(true);
  };

  return (
    <>
      <div
        onClick={handleCardClick}
        className="bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-violet-200 hover:-translate-y-1 transition-all duration-300 group cursor-pointer flex flex-col h-full"
      >
        {/* Banner */}
        <div className={`bg-gradient-to-br ${course.gradient} px-5 py-6 relative`}>
          <div className="flex items-start justify-between mb-4">
            <div className="w-11 h-11 flex items-center justify-center bg-white/20 rounded-xl backdrop-blur-sm">
              <i className={`${course.icon} text-white text-xl`} />
            </div>
            <div className="flex items-center gap-2">
              <span className={`${course.tagColor} text-white text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap`}>
                {course.tag}
              </span>
              <span className="bg-black/20 text-white text-[11px] font-medium px-2 py-1 rounded-full whitespace-nowrap">
                {course.level}
              </span>
            </div>
          </div>
          <h3 className="text-white font-bold text-base leading-snug mb-1">
            {highlightText(course.title, highlight)}
          </h3>
          <p className="text-white/70 text-xs leading-relaxed line-clamp-2">{course.subtitle}</p>
        </div>

        {/* Body */}
        <div className="p-5 flex flex-col flex-1">
          {/* Instructor */}
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 flex items-center justify-center bg-violet-100 rounded-full flex-shrink-0">
              <i className="ri-user-line text-violet-500 text-xs" />
            </div>
            <span className="text-sm text-slate-600 truncate">
              {highlightDark(course.instructor, highlight)}
              <span className="text-slate-300 mx-1.5">·</span>
              <span className="text-violet-600 font-semibold">
                {highlightDark(course.instructorCompany, highlight)}
              </span>
            </span>
          </div>

          {/* Topics */}
          <div className="flex flex-wrap gap-1.5 mb-4">
            {course.topics.map((t) => (
              <span
                key={t}
                className={`px-2 py-0.5 text-[11px] rounded-md transition-colors ${
                  highlight && t.toLowerCase().includes(highlight.toLowerCase())
                    ? 'bg-violet-100 text-violet-700 ring-1 ring-violet-300'
                    : 'bg-slate-100 text-slate-500'
                }`}
              >
                {highlightDark(t, highlight)}
              </span>
            ))}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-2.5 text-xs mb-4 flex-wrap">
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-amber-400" />
              <span className="font-bold text-[#0A1628]">{course.rating}</span>
              <span className="text-slate-400">({course.reviews})</span>
            </span>
            <span className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold whitespace-nowrap">
              <i className="ri-group-fill text-emerald-500 text-[11px]" />
              <span className="font-bold">{course.students.toLocaleString()}</span>
              <span className="text-[10px] font-medium">人在學</span>
            </span>
            <span className="flex items-center gap-1 text-slate-400">
              <i className="ri-time-line" />
              {course.duration}
            </span>
          </div>

          {/* Price + CTA */}
          <div className="mt-auto pt-3 border-t border-slate-100">
            {/* 会员免费标识 */}
            {hasMemberPrice ? (
              <div className="mb-3 flex items-center gap-1.5 rounded-lg border border-violet-100 bg-violet-50 px-2 py-1.5">
                <div className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
                  <i className="ri-vip-crown-fill text-xs text-violet-500" />
                </div>
                <span className="text-[11px] font-semibold text-violet-600">會員價 ${course.memberPrice}</span>
                <span className="ml-auto text-[10px] whitespace-nowrap text-violet-400">訂閱會員解鎖</span>
              </div>
            ) : null}

            {/* Price row */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex min-h-[32px] items-baseline gap-2">
                {isFree ? (
                  <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-sm font-bold text-emerald-600 border border-emerald-100">
                    免費
                  </span>
                ) : (
                  <>
                    <span className="text-xl font-bold text-[#0A1628]">${course.price}</span>
                    {hasDiscount ? (
                      <span className="text-sm text-slate-300 line-through">${course.originalPrice}</span>
                    ) : null}
                    {hasDiscount ? (
                      <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md">
                        {discountPct}% OFF
                      </span>
                    ) : null}
                  </>
                )}
              </div>
            </div>

            {/* Two CTA buttons */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); handleCardClick(); }}
                className="py-2.5 border border-slate-200 text-slate-600 text-xs font-semibold rounded-xl cursor-pointer whitespace-nowrap hover:border-violet-300 hover:text-violet-600 transition-all"
              >
                查看詳情
              </button>
              <button
                onClick={handleQuickBuy}
                className={`py-2.5 text-white text-xs font-bold rounded-xl cursor-pointer whitespace-nowrap active:scale-95 transition-all ${
                  isPurchased ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-violet-600 hover:bg-violet-700'
                }`}
              >
                {isPurchased ? '繼續學習' : '立即購買'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showModal && !isPurchased && (
        <CoursePaymentModal
          course={course}
          onClose={() => setShowModal(false)}
          onSuccess={() => setEnrolled(true)}
        />
      )}
    </>
  );
};

export default CourseCard;
